import { AppType } from "client/Api/Apps/common";
import {
	ReactElement,
	RefObject,
	useCallback,
	useRef,
	useState
} from "react";
import { useRenderManager } from "../../useRenderManager";
import { OneByOneTileContent } from "../OneByOneTileContent";
import { TwoByTwoTileContent } from "../TwoByTwoTileContent";
import { useRegisterTileUsage } from "../useRegisterTileUsage";
import { TileNavigationOptions, useGo } from "../useGo";
import { AlertManager, useAlerts } from "client/Hooks";
import { localize } from "client_legacy/ndaw/culture";
import { PowerBiPopout } from "./PowerBiPopout";
import { PowerBiLiveTileContent } from "./PowerBiLiveTileContent";
import { TileRenderState } from "../../RenderState/common";
import { useBaseNavigation } from "../useInvokeNavigation";
import { TileContextProvider } from "../../TileContextProvider";
import { Deferred, deferred } from "client/Misc/utils";
import { Coordinate } from "client/Misc/commonTypes";

export type PowerBiTileContentProps = {
	renderState: TileRenderState<AppType.PowerBi>;
	onEdit: (() => void) | undefined;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const PowerBiTileContent = (props: PowerBiTileContentProps): ReactElement => {
	const {
		renderState,
		onEdit,
		onOpenTileMenu
	} = props;

	const [navigationElement, navigateHandler] = usePowerBiTileNavigation(renderState, onEdit);

	const {
		dimension,
		data: {
			settings,
			isConfiguredCorrectly
		}
	 } = renderState;

	const tileContent: ReactElement = useRenderManager(
		dimension,
		() => (
			<PowerBiLiveTileContent
				embedUrlBase={settings.embedUrlBase}
				dashboardId={settings.dashboardId}
				powerBiTileId={settings.powerBiTileId}
				tokenInfo={settings.tokenInfo}
				isConfiguredCorrectly={isConfiguredCorrectly}
				onNavigate={navigateHandler}
				onOpenTileMenu={onOpenTileMenu}
			/>
		),
		[
			{ columnSpan: 1, rowSpan: 1 },
			() => (
				<OneByOneTileContent
					onClick={navigateHandler}
					onOpenTileMenu={onOpenTileMenu}
				/>
			)
		],
		[
			{ columnSpan: 2, rowSpan: 2 },
			() => (
				<TwoByTwoTileContent
					onClick={navigateHandler}
					onOpenTileMenu={onOpenTileMenu}
				/>
			)
		]
	);

	return (
		<>
			<TileContextProvider renderState={renderState}>
				{tileContent}
			</TileContextProvider>

			{navigationElement}
		</>
	);
};
PowerBiTileContent.displayName = "PowerBiTileContent";

export function usePowerBiTileNavigation(
	renderStateIsh: Pick<TileRenderState<AppType.PowerBi>, "data" | "dimension" | "interactionState">,
	onEdit: (() => void) | undefined
): [element: ReactElement | null, () => Promise<void> | void] {
	const [shouldShowDialog, setShouldShowDialog] = useState<boolean>(false);
	const pendingDialogCompletion: RefObject<Deferred<void> | null> = useRef<Deferred<void>>(null);

	const go: ((options: TileNavigationOptions) => void) = useGo(renderStateIsh);
	const registerTileUsage: (() => void) = useRegisterTileUsage(renderStateIsh);
	const alertManager: AlertManager = useAlerts();

	const closeDialogHandler = useCallback(
		(): void => {
			pendingDialogCompletion.current
				?.resolve();

			pendingDialogCompletion.current = null;
			setShouldShowDialog(false);
		},
		[]
	);

	const {
		dimension: {
			dimension: { columnSpan }
		},
		data: {
			id,
			name,
			settings
		}
	} = renderStateIsh;

	return useBaseNavigation(
		renderStateIsh,
		onEdit,
		[
			shouldShowDialog && settings.tokenInfo
				? (
					<PowerBiPopout
						tileId={id}
						name={name}
						embedUrlBase={settings.embedUrlBase}
						dashboardId={settings.dashboardId}
						powerBiTileId={settings.powerBiTileId}
						tokenInfo={settings.tokenInfo}
						onClose={closeDialogHandler}
					/>
				)
				: null,
			useCallback(
				async (): Promise<void> => {
					if (columnSpan <= 2) {
						registerTileUsage();

						if (!settings.tokenInfo) {
							alertManager.showError(localize("_CouldNotRetrieveTheTileFromPowerBi"));
							return;
						}

						pendingDialogCompletion.current
							?.resolve();

						const completion: Deferred<void> = deferred<void>();
						pendingDialogCompletion.current = completion;
						setShouldShowDialog(true);
						await completion.promise;
					} else {
						go({ inNewWindow: true });
					}
				},
				[
					alertManager,
					columnSpan,
					go,
					registerTileUsage,
					settings.tokenInfo
				]
			)
		]
	);
}
